import BaseEllipsisText from '@/components/base/BaseEllipsisText';
import useMenu, { type MenuUINode } from '@/hooks/useMenu';
import { useIntl } from '@/i18n';
import { history } from '@/router/navigate';
import { feedback } from '@/utils/feedback';
import { renderIcon } from '@/utils/icon';
import { moduleGateReasonText } from '@/utils/moduleGate';
import { LockOutlined } from '@ant-design/icons';

interface ModuleRailProps {
  activeModule?: string;
  /** 当前路由所属模块，与正在浏览的P2分开标记。 */
  currentModule?: string;
  onSelectModule: (code: string) => void;
  /** 菜单键 → 待办数；由布局层统一取一次传下来，避免一级栏与二级栏各发一次请求 */
  badges: Record<string, number>;
}

function railNodes(nodes: MenuUINode[]) {
  return nodes.filter((node) => node.kind === 'module' || node.path);
}

/**
 * 一个模块上的数字 ＝ 子树里每个页面待办数之和（PRD `navBadge` 的算法就是相加）。
 * 服务端只回页面级的值；层级之和一律在前端算，后端不知道一级栏长什么样。
 */
function sumOf(node: MenuUINode, badges: Record<string, number>): number {
  if (node.kind === 'page') return badges[node.code] ?? 0;
  return (node.children ?? []).reduce(
    (total, child) => total + sumOf(child, badges),
    0,
  );
}

/**
 * PRD 一级模块栏：只负责模块切换，不承载页面级菜单。
 *
 * 数字来自 `API-10-200`（`useMenuBadges`：登录取一次、60 秒轮询、切页补取、失败保留旧值）。
 * 样式口径见验收稿 D13：22×22 圆形、`#FFF0ED` 底、`#BD5B52` 字、1–99 显原数、超过 99 显 `99+`、0 不显示。
 *
 * ⚠️ `99+` 是**用户 2026-10-27 拍板保留**的，不是漏改：PRD 原型对菜单角标不做截断
 * （`MUOU-V1-PRD.html:121587`，那句原本约束的是页签角标；顶栏消息铃铛才用 `99+`，见 `:83543`）。
 */
export default function ModuleRail({
  activeModule,
  currentModule,
  onSelectModule,
  badges,
}: ModuleRailProps) {
  const { menuTree, selectedKeys } = useMenu();
  const intl = useIntl();

  return (
    <nav
      className="admin-module-rail flex flex-1 min-h-0 flex-col gap-0 overflow-y-auto p-2"
      data-testid="admin-module-rail"
    >
      {railNodes(menuTree).map((node) => {
        const isActive = node.kind === 'module' && node.code === activeModule;
        const isCurrent =
          node.kind === 'module'
            ? node.code === currentModule
            : selectedKeys?.includes(node.code);
        const badge = sumOf(node, badges);
        const handleClick = () => {
          // 锁着的模块只说明原因、不换栏也不跳页（PRD `goMod` 27760–27765）
          if (node.gated) {
            feedback.message.warning(
              moduleGateReasonText(intl, node.reason ?? null),
            );
            return;
          }
          if (node.kind === 'module') {
            // 一级模块只打开或切换P2，正文由具体页面菜单负责导航。
            onSelectModule(node.code);
            return;
          }
          if (node.path) history.push(node.path);
        };

        return (
          <div key={node.code}>
            <button
              type="button"
              className={`admin-module-rail-item relative flex w-full min-h-18 flex-col items-center justify-center gap-2 mb-1 py-2.5 px-0.5 border border-solid rounded-lg cursor-pointer text-[12px] leading-[15px] text-center ${
                isCurrent
                  ? 'is-current border-primary-border bg-[var(--muou-primary-a20)] text-primary font-medium hover:bg-[var(--muou-primary-a20)]'
                  : isActive
                  ? 'border-primary-border bg-primary-soft text-primary font-medium hover:bg-primary-soft'
                  : 'border-transparent bg-transparent text-secondary hover:bg-primary-soft hover:text-primary'
              } ${isActive ? 'is-active' : ''} ${
                node.gated ? 'is-locked' : ''
              }`}
              aria-current={isCurrent ? 'page' : undefined}
              aria-pressed={node.kind === 'module' ? isActive : undefined}
              aria-controls={
                node.kind === 'module' ? 'admin-submenu' : undefined
              }
              onClick={handleClick}
            >
              {renderIcon(node.icon)}
              <BaseEllipsisText title={node.title} />
              {/* 锁图标顶替角标：锁着的模块点不进去，待办数没有意义（PRD 27709） */}
              {node.gated ? (
                <span className="admin-module-rail-lock" aria-hidden>
                  <LockOutlined />
                </span>
              ) : (
                badge > 0 && (
                  <span
                    className="admin-module-rail-badge absolute top-1.5 right-1 inline-flex w-5.5 h-5.5 items-center justify-center border border-solid border-badge-border rounded-full bg-badge-bg text-badge-text text-[11px] font-normal leading-[1] pointer-events-none"
                    aria-hidden
                  >
                    {badge > 99 ? '99+' : badge}
                  </span>
                )
              )}
            </button>
          </div>
        );
      })}
    </nav>
  );
}
